import type { ProspectApplicationHome } from '../services/tenantApi';

export interface ApplicationHomeMapPin {
  unitId: string;
  label: string;
  pickLabel: string;
  latitude: number;
  longitude: number;
  propertyId: string | null;
}

const PRIORITY_LABELS = [
  'Top pick',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
];

export function shortlistPriorityLabel(rank: number): string {
  return PRIORITY_LABELS[rank - 1] ?? `${rank}th`;
}

function mapPickLabel(home: ProspectApplicationHome, hasLinkedApply: boolean): string {
  if (hasLinkedApply) {
    if (home.preferenceOrder != null) return `Pick ${home.preferenceOrder}`;
    return 'Added';
  }
  if (home.shortlistRank != null) return shortlistPriorityLabel(home.shortlistRank);
  return 'Shortlist';
}

/** Homes and labels shown on Apply to Homes map (not every list row). */
export function buildApplicationHomeMapPins(homes: ProspectApplicationHome[]): ApplicationHomeMapPin[] {
  const located = homes.filter(
    (home): home is ProspectApplicationHome & { latitude: number; longitude: number } =>
      home.latitude != null && home.longitude != null,
  );
  const hasLinkedApply = located.some((home) => home.linked);

  const visible = hasLinkedApply
    ? located.filter((home) => home.linked)
    : located.filter((home) => home.shortlistRank != null);

  return visible.map((home) => ({
    unitId: home.unitId,
    label: home.label,
    pickLabel: mapPickLabel(home, hasLinkedApply),
    latitude: home.latitude,
    longitude: home.longitude,
    propertyId: home.propertyId ?? null,
  }));
}

/** One marker pill (or a short combined label) when many units share a property. */
export function summarizeMapPinLabels(pickLabels: string[]): string[] {
  const unique = [...new Set(pickLabels.map((label) => label.trim()).filter(Boolean))];
  if (unique.length === 0) return ['Home'];
  if (unique.length === 1) return unique;

  const picks = unique
    .filter((label) => /^Pick \d+$/.test(label))
    .sort((a, b) => Number(a.replace('Pick ', '')) - Number(b.replace('Pick ', '')));

  const nonPickApply = unique.filter((label) => label === 'Added');
  const shortlistStyle = unique.filter(
    (label) => !/^Pick \d+$/.test(label) && label !== 'Added',
  );

  if (picks.length > 0 && shortlistStyle.length === 0 && nonPickApply.length === 0) {
    if (picks.length === 2) return [`${picks[0]} & ${picks[1]}`];
    return [`${picks.length} picks`];
  }

  if (picks.length === 0 && shortlistStyle.length > 0) {
    return ['Multiple homes'];
  }

  if (picks.length > 0) {
    if (picks.length === 2) return [`${picks[0]} & ${picks[1]}`];
    return [`${picks.length} picks`];
  }

  return ['Multiple homes'];
}

export function mapPinGroupKey(pin: Pick<ApplicationHomeMapPin, 'propertyId' | 'latitude' | 'longitude'>): string {
  if (pin.propertyId?.trim()) return `property:${pin.propertyId.trim()}`;
  return `geo:${pin.latitude.toFixed(5)},${pin.longitude.toFixed(5)}`;
}
